import {
  GetMyPersonalTasksQuery,
  GetMyPersonalTasksQueryVariables,
  OperationNames,
  PersonalTask,
  SortEnumType,
  TaskPriorityEnum,
  TaskStatusEnum,
  TaskTypeEnum,
  useDeleteTasksMutation,
  useGetMeQuery,
  useGetMyPersonalTasksQuery,
  useUpdatePersonalTasksMutation,
} from "graphql/generated/schema-types";
import _ from "lodash";
import { ManagePersonalTaskInputMapper } from "mappings/managePersonalTaskInput";
import React, { Dispatch, SetStateAction, useMemo, useState } from "react";
import { contextMenu, Item, Menu, Submenu } from "react-contexify";
import {
  Row,
  useExpanded,
  useFilters,
  useGroupBy,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import {
  GetPageInfoFromConnectionType,
  GetTotalCountFromConnectionType,
} from "selectors/graphql-connection";
import { GetFilterHandler } from "shared-components/Table/Handlers/HandleFilter";
import { GetPageChangeHandler } from "shared-components/Table/Handlers/HandlePageChange";
import { GetHandlePageCountChange } from "shared-components/Table/Handlers/HandlePageCountChange";
import { GetSortHandler } from "shared-components/Table/Handlers/HandleSort";
import {
  useCommonTableSideEffects,
  useInitializeConnectionData,
} from "shared-components/Table/hooks";
import {
  ReactTable,
  ReactTablePaginationState,
} from "shared-components/Table/ReactTable";
import { SelectHookCallback } from "shared-components/Table/sub-components/SelectHookCallback";
import { Alert, AlertType } from "shared-components/toast";
import { ManagePersonalTasksColumns } from "./Columns";
import {
  ContextMenuDialogs,
  ContextMenuDialogSelector,
} from "./Dialogs/ContextMenuDialogSelector";
import { TaskDetailsDialog } from "./Dialogs/TaskDetails";
import { PersonalTaskFilterReducer } from "./filter";

//Sent explicitly so the table's slicing never depends on the server's default page size.
const DEFAULT_PAGE_SIZE = 50;

export default function PersonalTasks() {
  const { data: meQuery } = useGetMeQuery({
    fetchPolicy: "cache-only",
  });
  const [selectedTasks, setSelectedTasks] = useState<PersonalTask[]>([
    {
      taskId: 0,
      taskName: "",
      notes: "",
      status: TaskStatusEnum.Scheduled,
      priority: TaskPriorityEnum.Low,
      startTimeScheduled: null,
      taskOwnerId: 0,
      taskType: TaskTypeEnum.Personal,
      endTimeScheduled: undefined,
    },
  ]);

  const paginationStateTuple = useState<ReactTablePaginationState>({
    totalCount: 0,
    rowsPerPage: DEFAULT_PAGE_SIZE,
    page: 0,
  });
  const [paginationState, setPaginationState] = paginationStateTuple;

  const { refetch, fetchMore, data, variables, loading } =
    useGetMyPersonalTasksQuery({
      variables: {
        pageSize: DEFAULT_PAGE_SIZE,
        order: { startTimeScheduled: SortEnumType.Desc },
      },
      notifyOnNetworkStatusChange: true,
    });

  const pageData = useInitializeConnectionData<PersonalTask>(
    data?.myPersonalTasks,
    paginationState,
  );

  const totalCount = useMemo(
    () => GetTotalCountFromConnectionType<PersonalTask>(data?.myPersonalTasks),
    [data?.myPersonalTasks],
  );

  const pageInfo = useMemo(
    () => GetPageInfoFromConnectionType<PersonalTask>(data?.myPersonalTasks),
    [data?.myPersonalTasks],
  );

  const handleContextMenu =
    (selected: PersonalTask[], row?: Row<PersonalTask>) =>
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      row?.toggleRowSelected(true);
      console.log(selected);
      contextMenu.show({
        id: "personaltask-context-menu",
        event: e,
        props: {
          selected: selected.includes(row!.original)
            ? selected
            : [...selected, row?.original],
        },
      });
      e.preventDefault();
    };

  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
  const handleRowClick = (rowClicked: any) => {
    if (!rowClicked?.isGrouped) {
      setSelectedTasks([rowClicked.original]);
      setTaskDetailsOpen(true);
    }
  };

  const markAs = (status: TaskStatusEnum, args: any) => {
    const tasks: PersonalTask[] = args?.props?.selected ?? [];
    setSelectedTasks(tasks);

    const updatedTasks = _.cloneDeep(tasks);
    updatedTasks.forEach((mpt) => {
      mpt.status = status;
    });
    callUpdatePersonalTask(updatedTasks);
  };

  const setPriority = (priority: TaskPriorityEnum, args: any) => {
    const tasks: PersonalTask[] = args?.props?.selected ?? [];
    setSelectedTasks(tasks);

    const updatedTasks = _.cloneDeep(tasks);
    updatedTasks.forEach((mpt) => {
      mpt.priority = priority;
    });
    callUpdatePersonalTask(updatedTasks);
  };

  const [updatePersonalTask] = useUpdatePersonalTasksMutation();
  const callUpdatePersonalTask = (tasks: PersonalTask[]) => {
    updatePersonalTask({
      variables: {
        inputs: tasks.map((task) => ManagePersonalTaskInputMapper(task)),
      },
    }).then(() => {
      setPaginationState((state) => ({ ...state, page: 0 }));
      refetch(variables);
    });
  };

  const [deleteTasks] = useDeleteTasksMutation();
  const handleDeleteTask = (selectedTasks: { selected: PersonalTask[] }) => {
    if (
      selectedTasks.selected.some(
        (item: PersonalTask) => item.taskOwnerId !== meQuery?.me?.userId,
      )
    ) {
      Alert({
        type: AlertType.ERROR,
        message: "You can only delete tasks that you have created",
      });
      return;
    }
    deleteTasks({
      variables: {
        taskIDs: selectedTasks?.selected?.map((task: PersonalTask) => {
          return task!.taskId!;
        }),
        taskType: TaskTypeEnum.Personal,
      },
      refetchQueries: [OperationNames.Query.getMyPersonalTasks],
    }).then(() => setPaginationState((state) => ({ ...state, page: 0 })));
  };

  const [contextMenuDialogState, setContextMenuDialogState] = useState(false);
  const [selectedDialog, setSelectedDialog] = useState<ContextMenuDialogs>(
    ContextMenuDialogs.AssignToUser,
  );
  const openDialog = (dialog: ContextMenuDialogs, args: any) => {
    setSelectedTasks(args.props.selected);
    setContextMenuDialogState(true);
    setSelectedDialog(dialog);
  };

  const onPageChange = useMemo(
    () =>
      GetPageChangeHandler(
        [paginationState, setPaginationState],
        fetchMore,
        pageInfo?.endCursor,
        variables,
      ),
    [fetchMore, pageInfo?.endCursor, paginationState, variables],
  );

  const handlePageCountChange = useMemo(
    () =>
      GetHandlePageCountChange(variables, refetch, [
        paginationState,
        setPaginationState,
      ]),
    [paginationState, setPaginationState, variables, refetch],
  );
  const paginationTuple = useMemo(
    () =>
      [paginationState, setPaginationState] as [
        ReactTablePaginationState,
        Dispatch<SetStateAction<ReactTablePaginationState>>,
      ],
    [paginationState],
  );
  const sortTable = useMemo(
    () => GetSortHandler(variables, refetch, paginationTuple),
    [paginationTuple, refetch, variables],
  );

  const filterTable = useMemo(
    () =>
      GetFilterHandler<
        GetMyPersonalTasksQuery,
        GetMyPersonalTasksQueryVariables,
        PersonalTask
      >(variables, refetch, PersonalTaskFilterReducer, paginationTuple),
    [paginationTuple, refetch, variables],
  );

  const personalTaskTableInstance = useTable(
    {
      columns: ManagePersonalTasksColumns,
      data: pageData,
      manualFilters: true,
      manualSortBy: true,
      initialState: {
        sortBy: [{ id: "startTimeScheduled", desc: true }],
      },
    },
    useFilters,
    useGroupBy,
    useSortBy,
    useExpanded,
    useRowSelect,
    SelectHookCallback(),
  );

  const {
    state: { groupBy },
  } = personalTaskTableInstance;

  useCommonTableSideEffects(
    paginationStateTuple,
    totalCount,
    variables,
    refetch,
    groupBy,
  );

  return (
    <React.Fragment>
      <ReactTable
        tableInstance={personalTaskTableInstance}
        onFilter={filterTable}
        onSort={sortTable}
        stateChangeDebounceDuration={500}
        paginationState={paginationState}
        onPageChange={onPageChange}
        onPageCountChange={handlePageCountChange}
        onRowContextMenu={handleContextMenu}
        onRowClick={(rowClicked) => () => handleRowClick(rowClicked)}
        loading={loading}
      />
      <ContextMenuDialogSelector
        dialog={selectedDialog}
        isOpen={contextMenuDialogState}
        setState={() => setContextMenuDialogState(false)}
        refetch={refetch}
        variables={variables}
        selectedTasks={selectedTasks}
        updatePersonalTask={updatePersonalTask}
        setPaginationState={setPaginationState}
      />
      <TaskDetailsDialog
        isOpen={taskDetailsOpen}
        handleClose={() => setTaskDetailsOpen(false)}
        selectedTask={selectedTasks[0]}
      />
      <Menu id={"personaltask-context-menu"} style={{ zIndex: 10000 }}>
        <Submenu label="Mark As" disabled>
          <Item onClick={(args) => markAs(TaskStatusEnum.Scheduled, args)}>
            Scheduled
          </Item>
          <Item onClick={(args) => markAs(TaskStatusEnum.Completed, args)}>
            Completed
          </Item>
          <Item onClick={(args) => markAs(TaskStatusEnum.Cancelled, args)}>
            Cancelled
          </Item>
        </Submenu>
        <Submenu label="Set Priority">
          <Item
            onClick={(args) => setPriority(TaskPriorityEnum.Immediate, args)}
          >
            Immediate
          </Item>
          <Item onClick={(args) => setPriority(TaskPriorityEnum.High, args)}>
            High
          </Item>
          <Item onClick={(args) => setPriority(TaskPriorityEnum.Medium, args)}>
            Medium
          </Item>
          <Item onClick={(args) => setPriority(TaskPriorityEnum.Low, args)}>
            Low
          </Item>
        </Submenu>
        <Submenu label="Assign To">
          <Item
            onClick={(args) =>
              openDialog(ContextMenuDialogs.AssignToUser, args)
            }
          >
            User
          </Item>
          <Item
            onClick={(args) =>
              openDialog(ContextMenuDialogs.AssignToGroup, args)
            }
          >
            Group
          </Item>
          <Item
            onClick={(args) =>
              openDialog(ContextMenuDialogs.AssignToSystem, args)
            }
          >
            System
          </Item>
        </Submenu>
        <Item
          onClick={(args) =>
            openDialog(ContextMenuDialogs.RescheduleTask, args)
          }
          disabled
        >
          Reschedule Task
        </Item>
        <Item onClick={(args) => openDialog(ContextMenuDialogs.EditTask, args)}>
          Edit Task
        </Item>
        <Item
          onClick={({ props }) =>
            handleDeleteTask(props as { selected: PersonalTask[] })
          }
        >
          Delete
        </Item>
      </Menu>
    </React.Fragment>
  );
}
